import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Section, Inner, SectionHeader, SectionTitle, SectionSubtitle, Divider, fadeInUp } from '../styles/v2.styled';

const Bg = styled(Section)`
  background: ${({ theme }) => theme.colors.neutral[0]};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BoardCard = styled.div`
  background: ${({ theme }) => theme.colors.neutral[0]};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  overflow: hidden;
  animation: ${fadeInUp} 0.5s ease both;
  animation-delay: ${({ $idx }) => $idx * 0.08}s;
`;

const BoardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[100]};
`;

const BoardTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.typography.fontFamily.semibold};
  font-size: 1.05rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.neutral[900]};
`;

const MoreLink = styled(Link)`
  font-size: 0.84rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary[700]};
  text-decoration: none;
`;

const PostList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const PostItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[50]};

  &:last-child {
    border-bottom: none;
  }
`;

const PostTitle = styled(Link)`
  min-width: 0;
  color: ${({ theme }) => theme.colors.neutral[700]};
  text-decoration: none;
  font-size: 0.92rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    color: ${({ theme }) => theme.colors.primary[700]};
  }
`;

const PostMeta = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.neutral[400]};
  font-size: 0.78rem;
`;

const FALLBACK_NEWS = [
  { title: 'AI 엔지니어링 과정 신규 모집 안내', meta: '공지' },
  { title: '실시간 온라인 강의 참여 전 체크리스트', meta: '공지' },
  { title: '프로젝트 제출 및 피드백 운영 안내', meta: '공지' },
];

const FALLBACK_FREE = [
  { title: '수강 전 학습 목표를 정리하는 방법', meta: '학습팁' },
  { title: '데이터 분석 실습 환경 준비하기', meta: '자료' },
  { title: '강사에게 질문을 남기는 좋은 방식', meta: '가이드' },
];

const safeJson = (res) => {
  if (!res.ok) return [];
  return res.text().then((text) => {
    if (!text || text.trim() === '') return [];
    try {
      return JSON.parse(text);
    } catch {
      return [];
    }
  });
};

const normalizePosts = (data, fallback) => {
  if (!Array.isArray(data) || data.length === 0) return fallback;
  return data.slice(0, 5).map((post) => ({
    title: post.notice_title || post.title || '제목 없음',
    meta: post.notice_name || post.name || '작성자',
    id: post.num || post.NUM || post.id || 0,
  }));
};

function BoardPreview() {
  const [newsData, setNewsData] = useState([]);
  const [freeData, setFreeData] = useState([]);

  useEffect(() => {
    fetch('/request/SelectNews')
      .then(safeJson)
      .then((d) => setNewsData(Array.isArray(d) ? d : []))
      .catch(() => {});

    fetch('/request/SelectFree')
      .then(safeJson)
      .then((d) => setFreeData(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const boards = [
    {
      title: '공지사항',
      data: normalizePosts(newsData, FALLBACK_NEWS),
      link: '/NewsBoard/0',
      type: 'notice',
    },
    {
      title: '자유게시판',
      data: normalizePosts(freeData, FALLBACK_FREE),
      link: '/FreeBoard/0',
      type: 'free',
    },
  ];

  return (
    <Bg $py="72px" $pyMobile="48px">
      <Inner>
        <SectionHeader>
          <Divider $center />
          <SectionTitle>소식과 학습 가이드</SectionTitle>
          <SectionSubtitle>강의 운영 공지와 학습에 필요한 안내를 확인하세요.</SectionSubtitle>
        </SectionHeader>

        <Grid>
          {boards.map((board, i) => (
            <BoardCard key={board.title} $idx={i}>
              <BoardHeader>
                <BoardTitle>{board.title}</BoardTitle>
                <MoreLink to={board.link} data-testid={`board-more-${board.type}`}>더보기</MoreLink>
              </BoardHeader>
              <PostList>
                {board.data.map((post, j) => (
                  <PostItem key={`${post.title}-${j}`}>
                    <PostTitle to={post.id ? board.link.replace('/0', `/${post.id}`) : board.link}>
                      {post.title}
                    </PostTitle>
                    <PostMeta>{post.meta}</PostMeta>
                  </PostItem>
                ))}
              </PostList>
            </BoardCard>
          ))}
        </Grid>
      </Inner>
    </Bg>
  );
}

export default BoardPreview;
